import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, Clock, FileUp, ShieldCheck } from "lucide-react";
import { useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ButtonLabel } from "../../components/ButtonLabel";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { apiRequest, describeApiError } from "../../lib/api";
import { currentPosition } from "../../lib/geo";
import { uploadKycDocument, useKyc, useKycCatalogue, useLibreWallet } from "../../lib/queries";
import type { ApiEnvelope, KycFile, KycFileDocumentKind } from "../../types";

/**
 * "Completa tu expediente": what lifts Kuspit's 3,000 UDI monthly deposit limit (call of
 * 2026-10-06) — identification front and back (a passport has no back), CURP, RFC, proof of address
 * no older than three months, and the transactional profile. Each file goes straight to Kuspit;
 * Aventuria keeps only that it was sent. Kuspit reviews in 7–10 days, and says per document what it
 * refused and why.
 */

const DOCUMENTS: Record<KycFileDocumentKind, { label: string; hint: string }> = {
  id_front: { label: "Identificación oficial (frente)", hint: "INE o pasaporte vigente, legible y completo." },
  id_back: { label: "Identificación oficial (reverso)", hint: "El reverso de tu INE." },
  curp: { label: "CURP", hint: "Descárgala en gob.mx/curp." },
  rfc: { label: "Constancia de RFC", hint: "Constancia de Situación Fiscal o cédula del RFC." },
  proof_of_address: { label: "Comprobante de domicilio", hint: "Luz, agua, teléfono o predial, de no más de 3 meses." }
};

const MAX_BYTES = 5 * 1024 * 1024;

export function KycFilePage() {
  usePageHeader("Tu expediente", "Completa tu expediente con Kuspit para aportar sin límite");
  const kyc = useKyc();
  const { wallet } = useLibreWallet();
  const queryClient = useQueryClient();
  const [identification, setIdentification] = useState<"ine" | "passport" | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string[] | null>(null);
  const inputs = useRef<Partial<Record<KycFileDocumentKind, HTMLInputElement | null>>>({});

  if (kyc.isLoading) return <div className="page-state"><Spinner label="Cargando tu expediente" /></div>;
  if (kyc.isError || !kyc.data) return <div className="page-state error"><CircleAlert aria-hidden="true" /><strong>No pudimos cargar tu expediente</strong></div>;
  const { file, contract } = kyc.data;
  const chosenIdentification = identification ?? file.identification ?? "ine";
  const editable = Boolean(contract) && file.status !== "approved";

  const upload = async (kind: KycFileDocumentKind, chosen: File | undefined) => {
    if (!chosen) return;
    setError(null); setNotice(null);
    if (chosen.size > MAX_BYTES) { setError(["El archivo pesa más de 5 MB. Comprímelo o tómale una foto más ligera."]); return; }
    setBusy(kind);
    try {
      const result = await uploadKycDocument(kind, chosen, kind === "id_front" || kind === "id_back" ? chosenIdentification : undefined);
      await queryClient.invalidateQueries({ queryKey: ["kyc"] });
      if (!result.data.accepted) setError([`Kuspit no aceptó ${DOCUMENTS[kind].label.toLowerCase()}: ${result.data.message ?? "revisa que sea legible"}.`]);
      else setNotice(result.data.needsBack ? "Recibido. Kuspit también necesita el reverso de tu identificación." : "Documento enviado a Kuspit.");
    } catch (cause) {
      setError(describeApiError(cause, "No pudimos enviar el documento."));
    } finally {
      setBusy(null);
      const input = inputs.current[kind];
      if (input) input.value = "";
    }
  };

  return (
    <div className="page-container" style={{ display: "grid", gap: 20, maxWidth: 860 }}>
      <FileStatusBanner file={file} hasContract={Boolean(contract)} />
      {wallet?.depositLimit?.applies && (
        <div className="card" style={{ padding: 16 }}>
          <strong>Límite actual de aportaciones</strong>
          <p className="field-hint" style={{ fontSize: 13, margin: "6px 0 0" }}>
            Mientras tu expediente no esté aprobado, Kuspit acepta hasta <span translate="no">{wallet.depositLimit.limitUdi.toLocaleString("es-MX")} UDIs</span> al mes
            (≈ <span translate="no">{mxn(wallet.depositLimit.limitMxn)}</span>). Este mes llevas <span translate="no">{mxn(wallet.depositLimit.usedMxn)}</span>.
            Una transferencia que lo rebase es devuelta por Kuspit a tu cuenta de origen.
          </p>
        </div>
      )}
      {notice && <div className="form-message" role="status">{notice}</div>}
      {error && <div className="form-error" role="alert">{error.join(" ")}</div>}

      {contract && (
        <section className="card" style={{ padding: 20, display: "grid", gap: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>Documentos</h3>
            <label className="field-block" style={{ minWidth: 200 }}>
              <span className="field-hint">Tu identificación es</span>
              <select className="standalone-input small" value={chosenIdentification} onChange={(event) => setIdentification(event.target.value as "ine" | "passport")} disabled={!editable || Boolean(file.identification)}>
                <option value="ine">INE (credencial para votar)</option>
                <option value="passport">Pasaporte</option>
              </select>
            </label>
          </div>
          {file.documents.filter((document) => document.required).map((document) => {
            const meta = DOCUMENTS[document.kind];
            const label = document.kind === "id_front" && chosenIdentification === "passport" ? "Pasaporte (hoja de datos)" : meta.label;
            return (
              <div key={document.kind} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 12, alignItems: "center", borderTop: "1px solid var(--line, #e5e7eb)", paddingTop: 12 }}>
                <div>
                  <strong>{label}</strong> <DocumentPill status={document.status} />
                  <span className="field-hint" style={{ fontSize: 12 }}>
                    {document.status === "rejected" && document.reason ? `Kuspit: ${document.reason}` : document.fileName ? document.fileName : meta.hint}
                  </span>
                </div>
                {editable && document.status !== "validated" && (
                  <>
                    <input ref={(element) => { inputs.current[document.kind] = element; }} type="file" accept="application/pdf,image/png,image/jpeg" hidden onChange={(event) => void upload(document.kind, event.target.files?.[0])} />
                    <button type="button" className={document.status === "missing" || document.status === "rejected" ? "primary-button small" : "secondary-button small"} disabled={busy !== null} onClick={() => inputs.current[document.kind]?.click()}>
                      <ButtonLabel busy={busy === document.kind} busyLabel="Enviando" label={document.status === "missing" ? "Subir" : "Reemplazar"} icon={<FileUp size={14} aria-hidden="true" />} />
                    </button>
                  </>
                )}
              </div>
            );
          })}
          <span className="field-hint">PDF, PNG o JPEG · máximo 5 MB. Se envían directo a Kuspit; Aventuria no guarda tus documentos.</span>
        </section>
      )}

      {contract && <TransactionalProfileForm done={file.transactionalProfile} editable={editable} />}
    </div>
  );
}

function mxn(value: number) {
  return value.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function DocumentPill({ status }: { status: KycFile["documents"][number]["status"] }) {
  const map = {
    missing: { label: "Pendiente", tone: "muted" }, submitted: { label: "Enviado", tone: "info" },
    validated: { label: "Validado", tone: "ok" }, rejected: { label: "Rechazado", tone: "bad" }
  } as const;
  return <span className={`pill ${map[status].tone}`}>{map[status].label}</span>;
}

function FileStatusBanner({ file, hasContract }: { file: KycFile; hasContract: boolean }) {
  if (!hasContract) {
    return (
      <div className="card" style={{ padding: 20 }}>
        <strong>Primero abre tu Wallet Libre</strong>
        <p className="field-hint" style={{ fontSize: 13 }}>Tu expediente se completa sobre tu cuenta con Kuspit.</p>
        <Link className="primary-button small" to="/wallet/libre/nueva">Abrir mi wallet</Link>
      </div>
    );
  }
  const content = {
    not_started: { icon: <ShieldCheck aria-hidden="true" />, title: "Completa tu expediente", body: "Con tus documentos y tu perfil transaccional, Kuspit quita el límite de 3,000 UDIs al mes." },
    in_progress: { icon: <ShieldCheck aria-hidden="true" />, title: "Tu expediente va avanzando", body: `Falta: ${file.missing.join(", ")}.` },
    submitted: { icon: <Clock aria-hidden="true" />, title: "En revisión con Kuspit", body: "Suele tardar de 7 a 10 días. Mientras tanto tu wallet funciona normal con el límite de 3,000 UDIs al mes; una aportación que lo rebase se devuelve a tu cuenta de origen." },
    approved: { icon: <CheckCircle2 aria-hidden="true" />, title: "Expediente aprobado", body: "Ya puedes aportar sin límite mensual." },
    rejected: { icon: <CircleAlert aria-hidden="true" />, title: "Kuspit pidió corregir tu expediente", body: file.note ?? "Revisa los documentos marcados y vuelve a subirlos." }
  }[file.status];
  return (
    <div className={`card${file.status === "rejected" ? " alert" : ""}`} style={{ padding: 20, display: "grid", gridTemplateColumns: "auto minmax(0,1fr)", gap: 14, alignItems: "start" }}>
      {content.icon}
      <div>
        <strong>{content.title}</strong>
        <p className="field-hint" style={{ fontSize: 13, margin: "6px 0 0" }}>{content.body}</p>
      </div>
    </div>
  );
}

function TransactionalProfileForm({ done, editable }: { done: boolean; editable: boolean }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ occupation: "", sectorId: "", activityId: "", activityLabel: "", monthlyIncomeId: "", fundsOriginId: "", incomeSourceId: "", operationsId: "", investmentId: "", businessActivity: false });
  const [activityFilter, setActivityFilter] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const sectors = useKycCatalogue("businessSectors");
  const activities = useKycCatalogue("activities", { idGiro: form.sectorId }, Boolean(form.sectorId));
  const income = useKycCatalogue("income");
  const origin = useKycCatalogue("fundsOrigin");
  const sources = useKycCatalogue("incomeSources");
  const operations = useKycCatalogue("operations");
  const investments = useKycCatalogue("investments");
  const filteredActivities = useMemo(() => {
    const needle = activityFilter.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().trim();
    return (activities.data ?? []).filter((option) => !needle || option.label.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().includes(needle)).slice(0, 200);
  }, [activities.data, activityFilter]);
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null); setSubmitting(true);
    try {
      const location = await currentPosition();
      await apiRequest<ApiEnvelope<{ file: KycFile }>>("/api/v1/kyc/file/transactional-profile", { method: "PUT", body: JSON.stringify({ ...form, ...location }) });
      await queryClient.invalidateQueries({ queryKey: ["kyc"] });
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof Error && !("code" in cause) ? [cause.message] : describeApiError(cause, "No pudimos enviar tu perfil."));
    } finally { setSubmitting(false); }
  };

  if (done && !saved) {
    return <section className="card" style={{ padding: 20 }}><strong>Perfil transaccional</strong> <span className="pill ok">Enviado</span><p className="field-hint" style={{ fontSize: 13 }}>Kuspit ya tiene tu perfil transaccional.</p></section>;
  }
  if (saved) return <section className="card" style={{ padding: 20 }}><div className="form-message" role="status">Perfil transaccional enviado a Kuspit.</div></section>;

  const select = (id: string, label: string, value: string, onChange: (value: string) => void, options: Array<{ id: string; label: string }> | undefined) => (
    <div className="field-block">
      <label htmlFor={id}>{label}</label>
      <select id={id} className="standalone-input" value={value} onChange={(event) => onChange(event.target.value)} required>
        <option value="">Selecciona</option>
        {(options ?? []).map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
      </select>
    </div>
  );

  return (
    <form className="card" style={{ padding: 20, display: "grid", gap: 14 }} onSubmit={submit}>
      <div>
        <h3 style={{ margin: 0 }}>Perfil transaccional</h3>
        <span className="field-hint" style={{ fontSize: 12 }}>Kuspit lo pide por regulación (CNBV) para conocer el origen y uso de tus recursos.</span>
      </div>
      <fieldset disabled={!editable || submitting} style={{ border: 0, padding: 0, margin: 0, display: "grid", gap: 14 }}>
        <div className="field-block">
          <label htmlFor="tp-occ">Ocupación</label>
          <input id="tp-occ" className="standalone-input" value={form.occupation} onChange={(event) => set("occupation", event.target.value)} placeholder="Ej. Diseñadora, contador, estudiante" required />
        </div>
        <div className="field-grid">
          {select("tp-sector", "Giro", form.sectorId, (value) => setForm((current) => ({ ...current, sectorId: value, activityId: "", activityLabel: "" })), sectors.data)}
          <div className="field-block">
            <label htmlFor="tp-act">Actividad</label>
            <input className="standalone-input small" placeholder={form.sectorId ? "Buscar actividad…" : "Primero elige el giro"} value={activityFilter} onChange={(event) => setActivityFilter(event.target.value)} disabled={!form.sectorId} aria-label="Buscar actividad" />
            <select id="tp-act" className="standalone-input" value={form.activityId} onChange={(event) => { const option = filteredActivities.find((entry) => entry.id === event.target.value); setForm((current) => ({ ...current, activityId: event.target.value, activityLabel: option?.label ?? "" })); }} disabled={!form.sectorId} required>
              <option value="">{activities.isLoading ? "Cargando…" : "Selecciona"}</option>
              {filteredActivities.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </div>
        </div>
        <div className="field-grid">
          {select("tp-income", "Ingreso mensual neto", form.monthlyIncomeId, (value) => set("monthlyIncomeId", value), income.data)}
          {select("tp-source", "Fuente de tus ingresos", form.incomeSourceId, (value) => set("incomeSourceId", value), sources.data)}
        </div>
        <div className="field-grid">
          {select("tp-origin", "Procedencia de los recursos que ahorrarás", form.fundsOriginId, (value) => set("fundsOriginId", value), origin.data)}
          {select("tp-ops", "Operaciones al mes", form.operationsId, (value) => set("operationsId", value), operations.data)}
        </div>
        {select("tp-inv", "Cuánto invertirás al mes", form.investmentId, (value) => set("investmentId", value), investments.data)}
        <label className="check-row"><input type="checkbox" checked={form.businessActivity} onChange={(event) => set("businessActivity", event.target.checked)} /> <span>Tengo actividad empresarial (persona física con actividad empresarial).</span></label>
      </fieldset>
      {error && <div className="form-error" role="alert">{error.join(" ")}</div>}
      {editable && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button type="submit" className="primary-button" disabled={submitting}><ButtonLabel busy={submitting} busyLabel="Enviando" label="Enviar perfil" /></button>
        </div>
      )}
    </form>
  );
}
